import DownloadIcon from "@mui/icons-material/Download";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import Disabled from "components/common/Disabled";
import * as config from "config";
import FileSaver from "file-saver";
import { useSnackbar } from "notistack";
import { useState } from "react";
import * as utils from "utils";

/**
 * Button component for downloading SOV templates
 *
 * @param {string} templateId ID of the template to download
 * @param {string} templateName Name to use for the downloaded file
 * @param {React.ReactNode} startIcon Icon to display at the start of the button
 * @param {string} label Button label text
 * @param {string} successMessage Message to display on successful download
 * @param {string} failMessage Message to display on failed download
 * @returns {React.ReactElement} DownloadTemplateButton component
 */
const DownloadTemplateButton = ({
  templateId,
  templateName,
  startIcon = <DownloadIcon />,
  label = "Template",
  successMessage = "Template downloaded successfully",
  failMessage = `Error downloading template. Please contact ${config.SUPPORT_EMAIL}`,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadTemplate = async () => {
    setIsDownloading(true);
    try {
      const response = await utils.authenticatedFetch(
        config.endpoints.files("download-template"),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            templateId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to download template");
      }

      const responseData = await response.json();
      const blobResponse = await fetch(responseData.url);
      const blob = await blobResponse.blob();
      FileSaver.saveAs(blob, `${templateName}.xlsx`);
      enqueueSnackbar(successMessage, { variant: "success" });
    } catch (error) {
      enqueueSnackbar(failMessage, {
        variant: "error",
        autoHideDuration: 10_000,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Disabled ifReadOnly>
      <Button
        variant={"outlined"}
        size={"small"}
        sx={{ color: theme.brand.accent[500] }}
        disabled={isDownloading}
        onClick={downloadTemplate}
        startIcon={startIcon}
      >
        {isDownloading ? "Downloading..." : label}
      </Button>
    </Disabled>
  );
};

export default DownloadTemplateButton;
